/**
 * Form floating-label and init-class management.
 *
 * Adds `field--show-floating-label` to a `.field` ancestor when its input
 * has focus or a non-empty value. Adds `input-init` once the field has
 * been touched, so a previously-styled "uninitialized" state never
 * flickers back after first interaction.
 *
 * Migrated from theme/js/modules/forms.js (IIFE → ESM, Phase 11).
 * Self-binds at module evaluation; no `appForms.init()` call required.
 *
 * Source path: theme/js/modules/forms.esm.js
 * Built name : ./forms.js (sibling imports must use the built name)
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 *
 * [Forms]
 */

import { log } from './log.js';

const FIELD_SELECTOR = '.field';
const INPUT_SELECTOR = '.field input';
const SHOW_LABEL_CLASS = 'field--show-floating-label';
const INIT_CLASS = 'input-init';

function getField(input) {
  return input.closest(FIELD_SELECTOR);
}

function syncLabelClass(input) {
  const field = getField(input);
  if (!field) return;
  const hasVal = input.value !== '';
  const isFocused = input === document.activeElement;
  if (isFocused || hasVal) {
    field.classList.add(SHOW_LABEL_CLASS);
  } else {
    field.classList.remove(SHOW_LABEL_CLASS);
  }
}

function refreshAll() {
  document.querySelectorAll(INPUT_SELECTOR).forEach((input) => {
    syncLabelClass(input);
    const field = getField(input);
    if (field) field.classList.add(INIT_CLASS);
  });
}

document.addEventListener('keyup', (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.matches(INPUT_SELECTOR)) return;
  syncLabelClass(target);
});

document.addEventListener('focusin', (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.matches(INPUT_SELECTOR)) return;
  const field = getField(target);
  if (!field) return;
  field.classList.add(SHOW_LABEL_CLASS);
  field.classList.add(INIT_CLASS);
});

document.addEventListener('focusout', (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.matches(INPUT_SELECTOR)) return;
  const field = getField(target);
  if (!field) return;
  if (target.value === '') field.classList.remove(SHOW_LABEL_CLASS);
});

// Re-scan on hydration and Turbo navigation so newly-rendered forms
// get the floating-label / parsley wiring.
document.addEventListener('ep:ready', refreshAll);
document.addEventListener('ep:change', refreshAll);

// Initial pass — `defer` is set on the entry loader, so the DOM is parsed.
refreshAll();

log('[Forms] registered');
